import React, { useEffect, useRef } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { motion, useAnimation, useInView } from "framer-motion";

// Generate dummy data: 20 images
const dummyImages = Array.from({ length: 50 }, (_, i) => ({
  id: i,
  src: `https://picsum.photos/400/300?random=${i + 1}`,
}));

// Single Gallery Item Component
const GalleryItem = ({ image, index, numCols = 5 }) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { threshold: 0.3, triggerOnce: false });

  // Column-based Y offset
  const col = index % numCols;
  const finalYOffset = (col % 2) * -50;

  // Randomized initial offsets
  const maxOffset = 100;
  const initialX = Math.random() * maxOffset - maxOffset / 2;
  const initialY = Math.random() * maxOffset - maxOffset / 2;

  // Entry animation
  const entryAnimation = {
    hidden: { x: initialX, y: initialY, opacity: 0 },
    visible: { x: 0, y: finalYOffset, opacity: 1 },
  };

  // Randomized bubbling animation
  const bubbleXOffset = Math.random() * 6 - 3;
  const bubbleYOffset = Math.random() * 6 - 3;
  const bubbleDuration = Math.random() * 3 + 5;

  const bubblingAnimation = {
    y: [
      finalYOffset,
      finalYOffset - bubbleYOffset,
      finalYOffset,
      finalYOffset + bubbleYOffset,
      finalYOffset,
    ],
    x: [0, bubbleXOffset, 0, -bubbleXOffset, 0],
    transition: {
      repeat: Infinity,
      repeatType: "mirror",
      duration: bubbleDuration,
      ease: "easeInOut",
    },
  };

  useEffect(() => {
    if (isInView) {
      controls.start("visible").then(() => {
        controls.start(bubblingAnimation);
      });
    } else {
      controls.start("hidden");
    }
  }, [controls, isInView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={entryAnimation}
      transition={{ type: "spring", stiffness: 100, damping: 30 }}
      style={{ width: "100%", height: "100%", zIndex: 1 }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingTop: "150%", // 3:2 aspect ratio (height = 1.5 * width)
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <img
          src={image.src}
          alt={`Gallery ${image.id}`}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>
    </motion.div>
  );
};


const Gallery = ({ numCols = 5, images = dummyImages }) => {
  // Get breakpoints from mui theme, if small gap is 8px, then large gap is 16px
  const [gap, setGap] = React.useState(16);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Adjust number of columns and gap based on screen size
  useEffect(() => {
    if (isMobile) {
      setGap(8);
    } else {
      setGap(16);
    }
  }, [isMobile]);

  return (
    <Box
      sx={{
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: `repeat(${numCols}, 1fr)`,
        gap: `${gap}px`,
        px: `${gap}px`,
        py: `64px`,
      }}
    >
      {images.map((image, index) => (
        <GalleryItem key={image.id} image={image} index={index} numCols={numCols} />
      ))}
    </Box>
  );
};

export default Gallery;
